import { createRevision, getRevisionById } from 'services/revisions';
import { IRevisionCreate } from 'utils/contracts';

export const SUPPORTED_IDF_VERSIONS = ['9.4', '23.1', '23.2', '24.1', '24.2', '25.1', '25.2', '26.1'];
export const PARSE_POLL_INTERVAL_MS = 2000;
export const PARSE_POLL_MAX_ATTEMPTS = 90;
const IDF_SNIFF_MAX_BYTES = 65536;

export type ZoneNameFixKind = 'extra' | 'missing' | 'duplicate';

export type ZoneNameFix = {
  name: string;
  fixed: string;
  kind: ZoneNameFixKind;
  removeSegs: string[];
  needles: string[];
};

const ZONE_NAME_FIX_KINDS = new Set<ZoneNameFixKind>(['extra', 'missing', 'duplicate']);

export type ParseDialogState = {
  open: boolean;
  revisionId: string | null;
  sniffedVersion: string | null;
  parseStatus: string | null;
  zoneCount: number;
  totalSurface: number | null;
  parseErrors: string[];
  nameFixes: ZoneNameFix[];
  needsForceConvert: boolean;
};

export class IdfParseError extends Error {
  code: 'UNSUPPORTED_IDF_VERSION' | 'PARSE_CREATE_FAILED' | 'PARSE_TIMEOUT';
  sniffedVersion?: string;

  constructor(
    code: IdfParseError['code'],
    message: string,
    sniffedVersion?: string
  ) {
    super(message);
    this.name = 'IdfParseError';
    this.code = code;
    this.sniffedVersion = sniffedVersion;
    Object.setPrototypeOf(this, IdfParseError.prototype);
  }
}

export const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const emptyParseDialog = (): ParseDialogState => ({
  open: false,
  revisionId: null,
  sniffedVersion: null,
  parseStatus: null,
  zoneCount: 0,
  totalSurface: null,
  parseErrors: [],
  nameFixes: [],
  needsForceConvert: false,
});

export const isZoneNameErrorCode = (code: string) =>
  code === 'REVISION_INVALID_PATTERN_ZONE_NAMES' ||
  code.startsWith('REVISION_INVALID_PATTERN_ZONE_NAMES_');

export const extractNameFixes = (revision: any): ZoneNameFix[] => {
  const raw =
    revision?.results?.error?.nameFixes ?? revision?.results?.warnings?.nameFixes;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((row) => {
    if (!row || typeof row.name !== 'string' || typeof row.fixed !== 'string') return [];
    if (!ZONE_NAME_FIX_KINDS.has(row.kind)) return [];
    if (!row.name || row.name === row.fixed) return [];
    return [
      {
        name: row.name,
        fixed: row.fixed,
        kind: row.kind,
        removeSegs: Array.isArray(row.removeSegs) ? row.removeSegs.map(String) : [],
        needles: Array.isArray(row.needles) ? row.needles.map(String) : [],
      },
    ];
  });
};

/** Snackbar key when Fix and Convert did not store the rewritten model. */
export const zoneNameFixFailureMessageKey = (errors: unknown): string | null => {
  if (!errors || typeof errors !== 'object') return null;
  const bag = errors as Record<string, unknown>;
  if (bag.IDF_UPLOAD_NOT_VISIBLE || bag.ZONE_NAME_FIX_UPLOAD_FAILED) {
    return 'IDF_LAB_NAME_FIX_UPLOAD_FAILED';
  }
  if (bag.ZONE_NAME_FIX_NONE) return 'IDF_LAB_NAME_FIX_NONE';
  if (bag.ZONE_NAME_FIX_FORBIDDEN) return 'IDF_LAB_NAME_FIX_FORBIDDEN';
  return null;
};

/**
 * Optional UX hint only (head+tail). Authoritative Version detection is server parser
 * (full file line scan). Do not gate upload on this.
 */
export const sniffIdfVersionFromFile = async (file: File): Promise<string | null> => {
  if (!file) return null;
  const head = await file.slice(0, IDF_SNIFF_MAX_BYTES).text();
  let window = head;
  if (file.size > IDF_SNIFF_MAX_BYTES) {
    const tailStart = Math.max(0, file.size - IDF_SNIFF_MAX_BYTES);
    const tail = await file.slice(tailStart, file.size).text();
    window = `${head}\n${tail}`;
  }
  const match = /Version\s*,\s*(?<major>\d+)\.(?<minor>\d+)/i.exec(window);
  if (!match?.groups) return null;
  return `${match.groups.major}.${match.groups.minor}`;
};

/**
 * Count Zone objects with Part of Total Floor Area = Yes (full file).
 * Supports same-line and multiline Basic Zone blocks.
 * Skips ZoneHVAC / ZoneList / … and non-object field values like WaterHeater's `Zone,`.
 */
export const countZonesInIdfFile = async (file: File): Promise<number> => {
  const text = await file.text();
  const lines = text.split(/\r?\n/);
  let count = 0;

  const isZoneObjectStart = (trimmed: string) =>
    /^Zone\s*,/i.test(trimmed) && !/^Zone\w/i.test(trimmed);

  const cleanToken = (raw: string) => {
    let s = raw.replace(/!.*$/, '').trim();
    if (s.endsWith(',') || s.endsWith(';')) s = s.slice(0, -1).trim();
    return s;
  };

  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i].trim();
    if (!isZoneObjectStart(trimmed)) continue;

    const fields: string[] = [];
    const rest = trimmed.replace(/^Zone\s*,\s*/i, '');
    let ends = /;\s*$/.test(trimmed.replace(/!.*$/, ''));
    if (rest && !rest.startsWith('!')) {
      const withoutComment = rest.replace(/!.*$/, '');
      const body = withoutComment.replace(/;\s*$/, '').trim();
      if (body) {
        const tokens = body.split(',').map((t) => t.trim());
        if (!ends && tokens.length > 0 && tokens[tokens.length - 1] === '') tokens.pop();
        fields.push(...tokens);
      }
    }

    if (!ends && fields.length === 0) {
      let peek = i + 1;
      while (peek < lines.length) {
        const n = lines[peek].trim();
        if (!n || n.startsWith('!')) {
          peek += 1;
          continue;
        }
        if (n === ',' || n === ';') {
          ends = true;
          fields.length = 0;
        }
        break;
      }
      if (ends && fields.length === 0) continue;
    }

    for (let j = i + 1; !ends && j < lines.length; j++) {
      const line = lines[j].trim();
      if (!line || line.startsWith('!')) continue;
      const withoutComment = line.replace(/!.*$/, '');
      ends = /;\s*$/.test(withoutComment);
      const body = withoutComment.replace(/;\s*$/, '').trim();
      if (!body) {
        if (ends) break;
        continue;
      }
      const tokens = body.split(',').map((t) => t.trim());
      if (!ends && tokens.length > 0 && tokens[tokens.length - 1] === '') tokens.pop();
      fields.push(...tokens);
      i = j;
      if (ends) break;
    }

    if (fields.length === 0) continue;
    const partRaw = cleanToken(fields[12] ?? fields[fields.length - 1] ?? '');
    if (/^Yes$/i.test(partRaw)) count += 1;
  }

  return count;
};

export const extractParseErrors = (
  revision: any,
  options?: { genericFallback?: boolean }
): string[] => {
  // Hard errors OR soft parser warnings (checked + continue)
  const parserError =
    revision?.results?.error?.message ?? revision?.results?.warnings?.message;
  if (Array.isArray(parserError)) {
    return parserError.map((e) => String(e)).filter(Boolean);
  }
  if (parserError && typeof parserError === 'object') {
    return Object.keys(parserError).filter(Boolean);
  }
  if (parserError) return [String(parserError)];
  if (options?.genericFallback !== false && revision?.status === 'failed') {
    return ['REVISION_PARSE_FAILED'];
  }
  return [];
};

export const pollUntilParsed = async (
  revId: string,
  projectId: any,
  organizationId: any
) => {
  for (let attempt = 0; attempt < PARSE_POLL_MAX_ATTEMPTS; attempt++) {
    const res = await getRevisionById(revId, projectId, organizationId);
    const revision = res?.data;
    const status = revision?.status;
    if (status === 'checked' || status === 'failed') return revision;
    await sleep(PARSE_POLL_INTERVAL_MS);
  }
  throw new IdfParseError('PARSE_TIMEOUT', 'PARSE_TIMEOUT');
};

export const createAndParseRevision = async ({
  file,
  projectId,
  organizationId,
  payload,
}: {
  file: File;
  projectId: any;
  organizationId: any;
  payload: IRevisionCreate;
}): Promise<ParseDialogState> => {
  // Version is detected by the server parser (full IDF). Client sniff is hint-only.
  const clientHintVersion = await sniffIdfVersionFromFile(file);

  const createdId = await createRevision(payload, projectId, organizationId, {
    labsParse: true,
  });
  if (!createdId) {
    throw new IdfParseError('PARSE_CREATE_FAILED', 'PARSE_CREATE_FAILED');
  }

  const revision = await pollUntilParsed(createdId, projectId, organizationId);
  const zoneCount = await countZonesInIdfFile(file);
  const surface =
    typeof revision?.totalSurface === 'number' ? revision.totalSurface : null;
  const parseErrors = extractParseErrors(revision);
  const parsedVersion =
    (typeof revision?.idf_version === 'string' && revision.idf_version) ||
    clientHintVersion ||
    null;

  return {
    open: true,
    revisionId: createdId,
    sniffedVersion: parsedVersion,
    parseStatus: revision?.status || null,
    zoneCount,
    totalSurface: surface,
    // Show popup for hard errors OR soft warnings (even when status is checked)
    parseErrors,
    nameFixes: extractNameFixes(revision),
    needsForceConvert: revision?.status === 'failed' && zoneCount > 0,
  };
};

/** Update existing revision with a new IDF (or metadata only) and wait for parser (same UX as create). */
export const updateAndParseRevision = async ({
  file,
  revisionId,
  projectId,
  organizationId,
  payload,
}: {
  file?: File | null;
  revisionId: string;
  projectId: any;
  organizationId: any;
  payload: IRevisionCreate;
}): Promise<ParseDialogState> => {
  const clientHintVersion = file ? await sniffIdfVersionFromFile(file) : null;
  const { updateRevision } = await import('services/revisions');

  const result = await updateRevision(
    { ...payload, ...(file ? { file } : {}) },
    revisionId,
    projectId,
    organizationId,
    { labsParse: true }
  );
  if (!result?.success) {
    throw new IdfParseError('PARSE_CREATE_FAILED', 'PARSE_CREATE_FAILED');
  }

  const revision = await pollUntilParsed(revisionId, projectId, organizationId);
  // Keep-existing-file edits have no local File — assume zones so Continue stays available.
  const zoneCount = file ? await countZonesInIdfFile(file) : 1;
  const surface =
    typeof revision?.totalSurface === 'number' ? revision.totalSurface : null;
  const parseErrors = extractParseErrors(revision);
  const parsedVersion =
    (typeof revision?.idf_version === 'string' && revision.idf_version) ||
    clientHintVersion ||
    null;

  return {
    open: true,
    revisionId,
    sniffedVersion: parsedVersion,
    parseStatus: revision?.status || null,
    zoneCount,
    totalSurface: surface,
    parseErrors,
    nameFixes: extractNameFixes(revision),
    needsForceConvert: revision?.status === 'failed' && zoneCount > 0,
  };
};
